import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  orderBy,
  getDocs,
} from "firebase/firestore";
import * as XLSX from "xlsx";

export default function ProductDetails() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);

  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const emptyForm = {
    prodname: "",
    prodcatid: "",
    unitId: "",
    rate: "",
    stock: "",
    barcode: "",
    priority: "",
    discount: "",
    type: "Veg",
    customizable: false,
    kot: 'Yes',
    codeno: ""
  };

  const [form, setForm] = useState(emptyForm);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return;
      const roleSnap = await getDoc(doc(db, "authroles", u.uid));
      if (roleSnap.exists()) {
        setPropertyId(roleSnap.data().propertyId);
      }
    });
    return () => unsub();
  }, []);

  /* ================= FETCH MASTER ================= */
  useEffect(() => {
    if (!propertyId) return;

    const catQ = query(
      collection(db, "productcats"),
      where("propertyId", "==", propertyId),

    );

    const unitQ = query(collection(db, "productunits"), where('propertyId', '==', propertyId));

    const unsub1 = onSnapshot(catQ, (snap) =>
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsub2 = onSnapshot(unitQ, (snap) =>
      setUnits(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsub1();
      unsub2();
    };
  }, [propertyId]);

  /* ================= FETCH Menu ItemsS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "products"),
      where("propertyId", "==", propertyId),
      orderBy("prodname")
    );

    const unsub = onSnapshot(q, (snap) => {
      setProducts(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!form.prodname || !form.rate) {
      return Toast.fire({ icon: "error", title: "Required fields missing" });
    }

    // 🔎 Check if codeno already exists
    const codeQuery = query(
      collection(db, "products"),
      where("propertyId", "==", propertyId),
      where("codeno", "==", form.codeno)
    );

    const codeSnap = await getDocs(codeQuery);

    // If adding new and found duplicate
    if (!editId && !codeSnap.empty) {
      return Toast.fire({
        icon: "error",
        title: "Code No already exists",
      });
    }

    // If editing, check duplicate except current document
    if (editId && !codeSnap.empty) {
      const duplicate = codeSnap.docs.find((d) => d.id !== editId);
      if (duplicate) {
        return Toast.fire({
          icon: "error",
          title: "Code No already exists",
        });
      }
    }

    const data = {
      ...form,
      rate: Number(form.rate),
      stock: Number(form.stock || 0),
      priority: Number(form.priority || 0),
      discount: Number(form.discount || 0),
      propertyId,
    };

    if (editId) {
      await updateDoc(doc(db, "products", editId), {
        ...data,
        updatedAt: Timestamp.now(),
      });
      Toast.fire({ icon: "success", title: "Menu Items Updated" });
    } else {
      await addDoc(collection(db, "products"), {
        ...data,
        createdAt: Timestamp.now(),
      });
      Toast.fire({ icon: "success", title: "Product Added" });
    }

    setOpen(false);
    setEditId(null);
    setForm(emptyForm);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Menu Items?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "products", id));
    Toast.fire({ icon: "success", title: "Deleted" });
  };

  const filtered = products.filter((p) =>
    p.prodname?.toLowerCase().includes(search.toLowerCase())
  );

  const catName = (id) =>
    categories.find((c) => c.id === id)?.catname || "";

  const unitName = (id) =>
    units.find((u) => u.id === id)?.unit || "";

  const handleDownloadFormat = () => {
    const sampleData = [
      {
        prodname: "",
        prodcatid: "",
        unitId: "",
        rate: "",
        stock: "",
        priority: "",
        codeno: "",
        type: "Veg",
        kot: "Yes",
        discount: ""
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");

    XLSX.writeFile(wb, "Product_Import_Format.xlsx");
  };



  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        Toast.fire({ icon: "error", title: "Excel is empty" });
        return;
      }

      try {
        for (const row of jsonData) {
          if (!row.prodname || !row.rate) continue;

          // Check duplicate Code No
          const codeQuery = query(
            collection(db, "products"),
            where("propertyId", "==", propertyId),
            where("codeno", "==", row.codeno || "")
          );

          const codeSnap = await getDocs(codeQuery);
          if (!codeSnap.empty) continue;

          await addDoc(collection(db, "products"), {
            prodname: row.prodname || "",
            prodcatid: row.prodcatid || "",
            unitId: row.unitId || "",
            rate: Number(row.rate || 0),
            stock: Number(row.stock || 0),
            priority: Number(row.priority || 0),
            codeno: row.codeno || "",
            discount: Number(row.discount || 0),
            type: row.type || "Veg",
            kot: row.kot || "Yes",
            propertyId,
            createdAt: Timestamp.now(),
          });
        }

        Toast.fire({ icon: "success", title: "Excel Imported Successfully" });
      } catch (error) {
        Toast.fire({ icon: "error", title: error.message });
      }
    };

    reader.readAsArrayBuffer(file);
  };


  return (
    <Box className=" p-4">
      <Card>
        <CardContent>
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              background: "linear-gradient(135deg, #f5f7fa, #ffffff)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              {/* Left Side - Title */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.5,
                }}
              >
                🍽 Menu Items
              </Typography>

              {/* Right Side - Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="center"
              >
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 2, textTransform: "none" }}
                  onClick={handleDownloadFormat}
                >
                  ⬇ Download Format
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  ⬆ Import Excel
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleImportExcel}
                  />
                </Button>

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 2,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  }}
                  onClick={() => {
                    setForm(emptyForm);
                    setEditId(null);
                    setOpen(true);
                  }}
                >
                  Add Item
                </Button>
              </Stack>
            </Stack>

            {/* Search Field */}
            <Box mt={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="🔍 Search menu items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Kot</TableCell>
                  <TableCell>Code No</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.prodname}</TableCell>
                    <TableCell>{catName(row.prodcatid)}</TableCell>
                    <TableCell>{unitName(row.unitId)}</TableCell>
                    <TableCell>{row.rate}</TableCell>
                    <TableCell>{row.stock}</TableCell>
                    <TableCell>{row.priority}</TableCell>
                    <TableCell>{row.kot}</TableCell>
                    <TableCell>{row.codeno}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setForm(row);
                          setEditId(row.id);
                          setOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ================= DIALOG ================= */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editId ? "Edit Menu Items" : "Add Menu Items"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Product Name"
              size="small"
              fullWidth
              value={form.prodname}
              onChange={(e) =>
                setForm({ ...form, prodname: e.target.value })
              }
              autoFocus="true"
            />

            <TextField
              select
              label="Category"
              size="small"
              fullWidth
              value={form.prodcatid}
              onChange={(e) =>
                setForm({ ...form, prodcatid: e.target.value })
              }
            >
              <MenuItem value="">Select Category</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.catname}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Unit"
              size="small"
              fullWidth
              value={form.unitId}
              onChange={(e) =>
                setForm({ ...form, unitId: e.target.value })
              }
            >
              <MenuItem value="">Select Unit</MenuItem>
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.unit}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Type"
              size="small"
              fullWidth
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value })
              }
            >
              <MenuItem value="Veg">Veg</MenuItem>
              <MenuItem value="Non Veg">Non Veg</MenuItem>
            </TextField>

            <TextField
              select
              label="Kot"
              size="small"
              fullWidth
              value={form.kot}
              onChange={(e) =>
                setForm({ ...form, kot: e.target.value })
              }
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>

            <TextField
              label="Rate"
              type="number"
              size="small"
              fullWidth
              value={form.rate}
              onChange={(e) =>
                setForm({ ...form, rate: e.target.value })
              }
            />

            <TextField
              label="Stock"
              type="number"
              size="small"
              fullWidth
              value={form.stock}
              onChange={(e) =>
                setForm({ ...form, stock: e.target.value })
              }
            />

            <TextField
              label="Priority"
              type="number"
              size="small"
              fullWidth
              value={form.priority}
              onChange={(e) =>
                setForm({ ...form, priority: e.target.value })
              }
            />

            <TextField
              label="Code No"
              size="small"
              fullWidth
              value={form.codeno}
              onChange={(e) =>
                setForm({ ...form, codeno: e.target.value })
              }
            />
            {/* 
            <FormControlLabel
              control={
                <Switch
                  checked={form.customizable}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      customizable: e.target.checked,
                    })
                  }
                />
              }
              label="Customizable"
            /> */}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
